// Conditionally import actioncable only on client-side to avoid SSR issues
let ActionCable: any = null;
if (typeof window !== 'undefined') {
  ActionCable = require('actioncable');
}

interface ChatWebSocketCallbacks {
  onMessage: (data: any) => void;
  onConnected: () => void;
  onDisconnected: () => void;
  onError?: (error: any) => void;
}

class ChatWebSocket {
  private cable: any | null = null;
  private channel: any | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 3000;
  private reconnectTimer: NodeJS.Timeout | null = null;
  private isRejected = false; // Track if subscription was rejected to prevent reconnect loops

  connect(token: string, sessionId: string, callbacks: ChatWebSocketCallbacks) {
    // Check if running on client-side
    if (typeof window === 'undefined' || !ActionCable) {
      console.warn('[ChatWebSocket] Cannot connect: ActionCable not available (SSR context)');
      return;
    }

    // Disconnect existing connection before creating new one
    if (this.cable || this.channel) {
      console.log('[ChatWebSocket] Cleaning up existing connection...');
      this.disconnect();
    }

    // Reset rejection flag on new connection attempt
    this.isRejected = false;

    // Get WebSocket URL from environment or default to Rails backend
    const wsUrl = process.env.NEXT_PUBLIC_WS_URL || 'ws://localhost:3010/cable';

    // Build cable URL with auth token parameter (required by backend)
    const cableUrl = `${wsUrl}?auth_token=${encodeURIComponent(token)}`;

    console.log('[ChatWebSocket] Connecting to:', wsUrl);

    try {
      // Create ActionCable consumer with auth token in URL
      this.cable = ActionCable.createConsumer(cableUrl);

      // Subscribe to ChatChannel with proper callback binding
      this.channel = this.cable.subscriptions.create(
        {
          channel: 'ChatChannel',
          session_id: sessionId
        },
        {
          connected: () => {
            console.log('[ChatWebSocket] Connected to ChatChannel');
            this.reconnectAttempts = 0;
            this.isRejected = false;
            if (callbacks && callbacks.onConnected) {
              callbacks.onConnected();
            }
          },

          disconnected: () => {
            console.log('[ChatWebSocket] Disconnected from ChatChannel');
            if (callbacks && callbacks.onDisconnected) {
              callbacks.onDisconnected();
            }
            // Only attempt reconnect if not rejected (prevents infinite loop)
            if (!this.isRejected) {
              this.attemptReconnect(token, sessionId, callbacks);
            }
          },

          received: (data) => {
            console.log('[ChatWebSocket] Message received:', data);

            // Guard against undefined callbacks
            if (!callbacks || !callbacks.onMessage) {
              console.warn('[ChatWebSocket] No message callback available');
              return;
            }

            // Handle different message types
            if (data.type === 'typing') {
              callbacks.onMessage({ type: 'typing', typing: data.typing });
            } else if (data.type === 'message') {
              callbacks.onMessage({ type: 'message', message: data.message });
            } else if (data.type === 'streaming_chunk') {
              callbacks.onMessage({
                type: 'streaming_chunk',
                chunk: data.chunk,
                message_id: data.message_id
              });
            } else if (data.type === 'streaming_done') {
              callbacks.onMessage({ type: 'streaming_done', message_id: data.message_id });
            } else {
              callbacks.onMessage(data);
            }
          },

          rejected: () => {
            console.error('[ChatWebSocket] Subscription rejected - stopping reconnection attempts');
            this.isRejected = true;

            // Clean up connection
            this.disconnect();

            // Notify error callback
            if (callbacks && callbacks.onError) {
              callbacks.onError(new Error('WebSocket subscription rejected'));
            }
          }
        }
      );

      // Verify subscription was created
      if (!this.channel) {
        throw new Error('Failed to create subscription channel');
      }

      console.log('[ChatWebSocket] Subscription created successfully');
    } catch (error) {
      console.error('[ChatWebSocket] Connection error:', error);
      if (callbacks && callbacks.onError) {
        callbacks.onError(error);
      }
      // Only attempt reconnect if not rejected
      if (!this.isRejected) {
        this.attemptReconnect(token, sessionId, callbacks);
      }
    }
  }

  private attemptReconnect(token: string, sessionId: string, callbacks: ChatWebSocketCallbacks) {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('[ChatWebSocket] Max reconnection attempts reached');
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * this.reconnectAttempts;

    console.log(
      `[ChatWebSocket] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`
    );

    this.reconnectTimer = setTimeout(() => {
      this.connect(token, sessionId, callbacks);
    }, delay);
  }

  sendMessage(content: string, language: string, pageContext?: any) {
    if (!this.channel) {
      console.error('[ChatWebSocket] Cannot send message: not connected');
      return false;
    }

    console.log('[ChatWebSocket] Sending message:', { content, language, pageContext });

    // Send message without action - will call receive() method on backend
    this.channel.send({
      content,
      language,
      page_context: pageContext
    });

    return true;
  }

  disconnect() {
    console.log('[ChatWebSocket] Disconnecting...');

    // Clear reconnect timer
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    // Unsubscribe from channel first
    if (this.channel) {
      try {
        this.channel.unsubscribe();
      } catch (error) {
        console.warn('[ChatWebSocket] Error unsubscribing channel:', error);
      }
      this.channel = null;
    }

    // Then disconnect cable
    if (this.cable) {
      try {
        this.cable.disconnect();
      } catch (error) {
        console.warn('[ChatWebSocket] Error disconnecting cable:', error);
      }
      this.cable = null;
    }

    this.reconnectAttempts = 0;
    // Don't reset isRejected here to maintain rejection state
    console.log('[ChatWebSocket] Disconnected successfully');
  }

  isConnected(): boolean {
    // Check if both cable and channel exist
    if (!this.cable || !this.channel) {
      return false;
    }

    // Try to check if connection is open, with fallback
    try {
      return this.channel.consumer?.connection?.isOpen() || false;
    } catch (error) {
      console.warn('[ChatWebSocket] Error checking connection status:', error);
      return false;
    }
  }
}

// Export singleton instance
export const chatWebSocket = new ChatWebSocket();
export default chatWebSocket;
